/**
 * pushHandoffEngine — turns an operator "Hand off" action in Push Mode
 * into a tracked, owned FollowUpTask routable through handoffRouting.ts.
 *
 * Always:
 *   • creates a real FollowUpTask with canonical routing fields
 *     (assignedToRoleKey OR assignedToUserId + assignedToLabel)
 *   • links the source operational object (jobId)
 *   • appends a structured "Handed off" entry to the job's activity history
 *
 * Pure: assembles the objects, no I/O, fully testable.
 */

import type { Job } from "./types";
import type {
  FollowUpTask,
  FollowUpType,
  HandoffIntent,
} from "./opportunityTypes";
import type { UserRole } from "./RoleContext";

export interface PushHandoffContext {
  reasonFragment: string;
  sourceLabel?: string;
}

export interface PushHandoffInputs {
  job: Job;
  context: PushHandoffContext;
  /** Display label (always present). */
  assignee: string;
  /** Canonical role key when routing by seat. */
  assigneeRoleKey?: UserRole | null;
  /** Direct-user id when routing to a specific person. */
  assigneeUserId?: string | null;
  /** ISO date string. */
  dueDate: string;
  /** Who initiated the hand-off (for audit). */
  initiatedBy: string;
  /** Optional Phase-2 declared intent. Falls through to inference if omitted. */
  intent?: HandoffIntent;
  /** Optional Phase-2 action linkage. */
  actionKey?: string;
  actionLabel?: string;
  actionPayload?: unknown;
  /** Phase-3 lineage: id of the prior hand-off when chaining. */
  originHandoffId?: string;
  /** Phase-3 lineage: how this hand-off came to be. Defaults to "manual". */
  workflowSourceType?: "manual" | "system" | "ai_suggested" | "chained";
  /** Phase-3 lineage: id of the originating record (e.g. wrap id, signal id). */
  workflowSourceId?: string;
}

export interface PushHandoffResult {
  task: FollowUpTask;
  activityEntry: NonNullable<Job["activityHistory"]>[number];
}

/** Default due date: tomorrow at 09:00 local. */
export function defaultPushHandoffDueDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

export function buildPushHandoff(inputs: PushHandoffInputs): PushHandoffResult {
  const { job, context, assignee, assigneeRoleKey, assigneeUserId, dueDate, initiatedBy } = inputs;
  const now = new Date().toISOString();
  const id = `FU-HO-${Date.now()}`;

  const opportunityAnchor = job.id;

  const baseDisposition = {
    reason: "other" as never,
    mainReasonNoDemoText: `Push-Mode hand-off ${context.reasonFragment}`.trim(),
    objections: [],
    nextSteps: "",
    followUpDateTime: dueDate,
    completedAt: now,
    completedBy: initiatedBy,
  } as unknown as FollowUpTask["disposition"];

  const task: FollowUpTask = {
    id,
    opportunityId: opportunityAnchor,
    type: "rep" as FollowUpType,
    assignedTo: assignee,
    dueDate,
    status: "pending",
    notes: `Push-Mode hand-off ${context.reasonFragment}.${context.sourceLabel ? ` Source: ${context.sourceLabel}.` : ""} Job ${job.id} — ${job.customerName}.`,
    createdAt: now,
    disposition: baseDisposition,
    customerName: job.customerName,
    customerPhone: job.phone || "",
    priority: "high",
    source: "manual",
    isAutomated: false,
    // ── Canonical routing fields ──
    assignedToLabel: assignee,
    assignedToRoleKey: assigneeRoleKey ?? undefined,
    assignedToUserId: assigneeUserId ?? undefined,
    // ── Source linkage ──
    jobId: job.id,
    // ── Phase-2 actionable hand-off framework (all optional) ──
    handoffIntent: inputs.intent,
    actionKey: inputs.actionKey,
    actionLabel: inputs.actionLabel,
    actionPayload: inputs.actionPayload,
    // ── Phase-3 workflow lineage (all optional) ──
    originHandoffId: inputs.originHandoffId,
    workflowSourceType: inputs.workflowSourceType ?? "manual",
    workflowSourceId: inputs.workflowSourceId,
  };

  const routingDescriptor = assigneeRoleKey
    ? `role:${assigneeRoleKey}`
    : assigneeUserId
      ? `user:${assigneeUserId}`
      : `legacy:${assignee}`;

  const activityEntry = {
    id: `act-${Date.now()}`,
    date: now.slice(0, 10),
    user: initiatedBy,
    action: `Handed off — follow-up ${id} assigned to ${assignee} (${routingDescriptor}), due ${dueDate.slice(0, 10)} (${context.reasonFragment}).`,
    noteType: "system" as const,
  };

  return { task, activityEntry };
}
